import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Bot, Radio, GraduationCap } from "lucide-react";
import { MaskedLine, Reveal, Eyebrow } from "@/components/Reveal";
import PricingTeaser from "@/components/PricingTeaser";
import Performance from "@/components/Performance";
import { SERVICES } from "@/data/site";

const SERVICE_ICONS = { bots: Bot, signals: Radio, education: GraduationCap };
const INTEREST_MAP = {
    bots: "Forex Trading Bots",
    signals: "Forex Signals",
    education: "Trading Education",
};

export default function Services() {
    return (
        <>
            <section data-testid="services-hero" className="relative overflow-hidden">
                <div className="pointer-events-none absolute -top-40 right-[-10%] h-[30rem] w-[30rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="mx-auto max-w-7xl px-6 pt-40 pb-20 md:px-10 md:pt-48 md:pb-28">
                    <MaskedLine delay={0.05}>
                        <Eyebrow>Services</Eyebrow>
                    </MaskedLine>
                    <h1 className="mt-8 max-w-4xl font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-balance">
                        <MaskedLine delay={0.2}>Everything a serious trader</MaskedLine>
                        <MaskedLine delay={0.34}>
                            needs. <span className="font-editorial italic font-light text-gold">Nothing they don't.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.6} className="mt-10 max-w-2xl">
                        <p className="text-base md:text-lg leading-relaxed text-zinc-400">
                            Bots that execute with machine discipline, signals filtered down to genuine
                            conviction, and education that makes you dangerous for the right reasons —
                            all focused on commodities, stocks and ETFs.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 pb-28 md:px-10 md:pb-40">
                <div className="space-y-24 md:space-y-36">
                    {SERVICES.map((s, i) => {
                        const Icon = SERVICE_ICONS[s.id];
                        const flip = i % 2 === 1;
                        return (
                            <div
                                key={s.id}
                                id={s.id}
                                data-testid={`service-section-${s.id}`}
                                className="scroll-mt-32 grid items-center gap-12 lg:grid-cols-12"
                            >
                                <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                                    <div className="group overflow-hidden border border-white/10">
                                        <img
                                            src={s.image}
                                            alt={s.name}
                                            className="h-72 w-full object-cover opacity-75 transition-transform duration-700 ease-out group-hover:scale-105 md:h-96"
                                        />
                                    </div>
                                </Reveal>
                                <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                                    <Reveal>
                                        <div className="flex items-center gap-3">
                                            <span className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold">
                                                <Icon size={19} />
                                            </span>
                                            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                                Chapter 0{i + 1}
                                            </span>
                                        </div>
                                        <h2 className="mt-7 font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                                            {s.name}
                                        </h2>
                                        <p className="mt-3 font-editorial italic font-light text-xl text-gold">
                                            {s.tagline}
                                        </p>
                                        <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400">
                                            {s.description}
                                        </p>
                                    </Reveal>
                                    <Reveal delay={0.12}>
                                        <ul className="mt-8 space-y-3">
                                            {s.features.map((f) => (
                                                <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                                                    <Check size={16} className="mt-0.5 shrink-0 text-gold" />
                                                    {f}
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            to={`/contact?interest=${encodeURIComponent(INTEREST_MAP[s.id])}`}
                                            data-testid={`service-enquire-${s.id}`}
                                            className="mt-10 inline-flex items-center gap-2 rounded-full border border-gold/40 px-8 py-3.5 text-sm text-gold transition-colors duration-300 hover:bg-gold hover:text-black"
                                        >
                                            Enquire about {s.name.toLowerCase()} <ArrowUpRight size={15} />
                                        </Link>
                                    </Reveal>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <Performance />

            <PricingTeaser />

            <section data-testid="services-cta" className="border-t border-white/10 bg-coal/30">
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
                    <Reveal className="flex flex-wrap items-center justify-between gap-10">
                        <div>
                            <Eyebrow>Not sure where to start?</Eyebrow>
                            <h2 className="mt-5 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
                                Talk to the desk.
                            </h2>
                            <p className="mt-4 max-w-lg text-sm leading-relaxed text-zinc-400">
                                Tell us where you are in your trading journey and we will point you at the
                                right instrument — even if that answer is 'education first'.
                            </p>
                        </div>
                        <Link
                            to="/contact"
                            data-testid="services-cta-contact"
                            className="inline-flex items-center gap-2 rounded-full bg-gold px-10 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-gold-light"
                        >
                            Get in touch <ArrowUpRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
